import React from "react";
import { Button, Col, Row } from "react-bootstrap";
import { Strings } from "../../resources/i18n/i18n";
import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
  tableCellClasses,
  styled,
  TableCell,
  Paper,
} from "@mui/material";
import SearchFilter from "../CustomUIComponents/SearchFilter/SearchFilter";
import Pagination from "../CustomUIComponents/Pagination/Pagination";
import {
  CONFIGURATION_DATE_TIME_FORMAT,
  DATE_TYPE,
  DEFAULT_DATE_FORMAT,
  DEFAULT_DURATION,
  DOWNLOAD_FILE_TYPE,
  SEARCH_FILTER_FIELDS,
  SEARCH_FILTER_TYPE,
} from "../../constants/appConstants";
import moment from "moment";
import {
  DEFAULT_PAGINATION_ACTIVE_PAGE,
  DEFAULT_PAGINATION_RECORDS_COUNT,
} from "../CustomUIComponents/Pagination/PaginationConstants";
import { COLLECTION_EXPENSE_STATUS, EXPENSE_HISTORY_COLUMNS } from "./Constants";
import { colors } from "../../resources/styles/themes";
import PDFdownload from "../../resources/images/ic_download_pdf.svg";
import CSVdownload from "../../resources/images/ic_download_csv.svg";

class ExpenseHistoryDetails extends React.Component {
  constructor(props) {
    super(props);
    this._columns = EXPENSE_HISTORY_COLUMNS;
    const searchFilter = props?.searchFilter || {};
    this.state = {
      ExpenseFilters: {
        fromDate: searchFilter?.fromDate || DEFAULT_DURATION.fromDate,
        toDate: searchFilter?.toDate || DEFAULT_DURATION.toDate,
        configType: searchFilter?.configType || "",
        status: searchFilter?.status || "",
      },
      activePage: searchFilter?.offset || DEFAULT_PAGINATION_ACTIVE_PAGE,
      recordsPerPage: searchFilter?.limit || DEFAULT_PAGINATION_RECORDS_COUNT,
    };
  }

  componentDidMount() {
      this.handleOnClickSearchFilter(false);
  }

  handleOnClickSearchFilter = (isDefaultOffset = false) => {
    isDefaultOffset &&
      this.setState({ activePage: DEFAULT_PAGINATION_ACTIVE_PAGE });

    this.props.handleExpenseSearchFilter({
      searchCriteria: {
        fromDate:  this.state.ExpenseFilters.fromDate ,
        toDate: this.state.ExpenseFilters.toDate,
        configType: this.state.ExpenseFilters.configType,
        status: this.state.ExpenseFilters.status,
      },
      limit: this.state.recordsPerPage,
      offset: isDefaultOffset
        ? DEFAULT_PAGINATION_ACTIVE_PAGE
        : this.state.activePage,
    });
  };

  handleOnClickResetFilter = () => {
    this.setState(
      {
        ExpenseFilters: {
          fromDate: DEFAULT_DURATION.fromDate,
          toDate: DEFAULT_DURATION.toDate,
          configType: "",
          status: "",
        },
        recordsPerPage: DEFAULT_PAGINATION_RECORDS_COUNT,
        activePage: DEFAULT_PAGINATION_ACTIVE_PAGE,
      },
      () => this.handleOnClickSearchFilter(true)
    );
  };

  handleFilterChange = (fieldname) => (event) => {
    const value = event.target.value;
    this.setState({
      ExpenseFilters: { ...this.state.ExpenseFilters, [fieldname]: (Strings("status")=== value || Strings("expenseType") === value) ? null : value },
    });
  };

  onPageChange = ({ page, pageLength }) => {
    this.setState(
      { activePage: page, recordsPerPage: pageLength },
      () => this.handleOnClickSearchFilter(false)
    );
  };

  handleDateChange = (key) => (dateMoment) => {
    let DateValue = "";
    switch (key) {
      case DATE_TYPE.FROM_DATE:
        if (dateMoment.isAfter(this.state.ExpenseFilters.toDate)) {
          DateValue = this.state.ExpenseFilters.fromDate;
        } else {
          dateMoment.set({ hour: 0, minute: 0, second: 0 });
          DateValue = dateMoment.format(DEFAULT_DATE_FORMAT);
        }
        break;
      case DATE_TYPE.TO_DATE:
        if (dateMoment.isBefore(this.state.ExpenseFilters.fromDate)) {
          DateValue =
            moment(dateMoment).diff(
              this.state.ExpenseFilters.fromDate,
              "days"
            ) === 0
              ? this.state.ExpenseFilters.fromDate
              : this.state.ExpenseFilters.toDate;
        } else {
          DateValue = dateMoment.format(DEFAULT_DATE_FORMAT);
        }
        break;
      default:
    }
    this.setState({
      ExpenseFilters: { ...this.state.ExpenseFilters, [key]: DateValue },
    });
  };

  renderSearchFilter = () => {
    const expenseFilters = [
      {
        type: SEARCH_FILTER_TYPE.DATE,
        fromDate: this.state.ExpenseFilters.fromDate,
        handleCalenderChange: this.handleDateChange,
        toDate: this.state.ExpenseFilters.toDate,
        fieldname: SEARCH_FILTER_FIELDS.DATE,
        column: 4,
        paddingTop: true,
      },
      {
        type: SEARCH_FILTER_TYPE.SELECTION,
        fieldname: "configType",
        name: "configType",
        column: 2,
        Value: this.state.ExpenseFilters.configType || "",
        handleOnChange: (event) => this.handleFilterChange("configType")(event),
        filterDropdownList: [Strings("expenseType"), ...(this.props.expenseTypes || []).map((et) => et.expenseType)],
        paddingTop: true,
      },
      {
        type: SEARCH_FILTER_TYPE.SELECTION,
        fieldname: "status",
        name: "status",
        column: 2,
        Value: this.state.ExpenseFilters.status || "",
        handleOnChange: (event) => this.handleFilterChange("status")(event),
        filterDropdownList: [Strings("Status"), ...COLLECTION_EXPENSE_STATUS],
        paddingTop: true,
      },
    ];
    return (
      <SearchFilter
        filters={expenseFilters}
        handleOnClickSearchFilter={this.handleOnClickSearchFilter}
        handleOnClickResetFilter={this.handleOnClickResetFilter}
        paddingRight={0}
        paddingTop={2}
        removeLeftPadding={true}
      />
    );
  };

  renderTableData = (row, key) => {
    switch (key) {
      case "createdAt":
        return moment(row[key]).format(CONFIGURATION_DATE_TIME_FORMAT);
      case "revertPercentage": {
        const isPartialRevert = row["entryType"] === "PARTIALLY_REVERTED" || row["entryType"] === "REVERTED" ;
        const hasValue = row[key] != null;
        return isPartialRevert && hasValue ? row[key] : Strings("NA");
      }
      case "entryType":
        return row[key] ? Strings(row[key]) : Strings("NA");
      case "type":
      case "createdBy":
        return row[key] || Strings("NA");
      default:
        return row[key] ? Strings(row[key]) : Strings("NA");
    }
  };

  renderCustomDataGrid = (columns, rows) => {
    const StyledTableCell = styled(TableCell)(({ theme }) => ({
      [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.divider,
        color: theme.palette.common.black,
      },
      [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
      },
    }));

    const StyledTableRow = styled(TableRow)(({ theme }) => ({
      "&:nth-of-type(even)": {
        backgroundColor: theme.palette.action.hover,
      },
      "&:last-child td, &:last-child th": {
        border: 0,
      },
    }));

    return (
      <div className='d-flex justify-content-center mx-3 mb-3'>
        <TableContainer
          component={Paper}
          className='d-flex justify-content-center'
        >
          <Table aria-label='customized table'>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <StyledTableCell
                    key={column.key}
                    className={"header px-2"}
                    title={column.name}
                  >
                    <div>{column.name}</div>
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <StyledTableRow key={row.id} hover>
                  {columns.map((column) => (
                    <StyledTableCell
                      key={column.key}
                      className={"px-2"}
                      component='th'
                      scope='row'
                      title={row[column.key]}
                      style={{
                        maxWidth: column.maxWidth,
                        minWidth: column.minWidth,
                      }}
                    >
                      <div
                        className={"text-start text-break"}
                        style={{
                          color: colors.textColor20,
                          fontSize: 17,
                          fontFamily: "Roboto-Regular",
                        }}
                      >
                        {this.renderTableData(row, column.key)}
                      </div>
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  };

  render() {
    return (
      <>
        <Row className='heading'>{Strings("ExpenseHistory")}</Row>
        <div className='minimartList container-fluid ListContainer'>
          <div className='mx-3 d-flex align-items-end'>
            <div className='flex-grow-1'>{this.renderSearchFilter()}</div>
            <div className='d-flex align-items-center pb-1'>
              <Button
                className='downloadButtons me-3'
                onClick={() => this.props.handleDownload(DOWNLOAD_FILE_TYPE.PDF)}
              >
                <img src={PDFdownload} alt='' />
              </Button>
              <Button
                className='downloadButtons me-1'
                onClick={() => this.props.handleDownload(DOWNLOAD_FILE_TYPE.CSV)}
              >
                <img src={CSVdownload} alt='' />
              </Button>
            </div>
          </div>
          <div className='userListTable mt-4'>
            {this.props.rows.length > 0 ? (
              <div>
                {this.renderCustomDataGrid(this._columns, this.props.rows)}
                <Col md={12} className='pt-3'>
                  <Pagination
                    activePage={this.state.activePage}
                    recordsPerPage={this.state.recordsPerPage}
                    numberOfPages={this.props.numberOfPages}
                    onPageChange={this.onPageChange}
                    dropup={true}
                  />
                </Col>
              </div>
            ) : (
              <div className='noRecordsFound'>{Strings("NoRecordsFound")}</div>
            )}
          </div>
        </div>
      </>
    );
  }
}

export default ExpenseHistoryDetails;
